import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";

import { workerPointHistoriesQuery } from "@/components/model/user/_graphql";
import { showNotification } from "@/util/userNotifications";

const PAGE_SIZE = 20;
const FETCH_ERROR_MESSAGE =
  "持ち点変動履歴の取得に失敗しました。時間をおいて再度お試しください。";

type UseWorkerPointHistoriesOptions = {
  /** true のとき GraphQL を呼ばない（モーダル未表示時の無駄なリクエストを避ける） */
  skip?: boolean;
};

/** ワーカー自身の持ち点変動履歴をカーソルページネーションで取得する */
export function useWorkerPointHistories(
  options: UseWorkerPointHistoriesOptions = {},
) {
  const { skip = false } = options;
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const { data, loading, error, fetchMore } = useQuery(
    workerPointHistoriesQuery,
    {
      variables: { first: PAGE_SIZE, after: null },
      skip,
    },
  );

  useEffect(() => {
    if (error)
      showNotification({ type: "error", message: FETCH_ERROR_MESSAGE });
  }, [error]);

  const edges = data?.workerPointHistories?.edges ?? [];
  const hasNextPage =
    data?.workerPointHistories?.pageInfo?.hasNextPage ?? false;
  const endCursor = data?.workerPointHistories?.pageInfo?.endCursor ?? null;

  const fetchMoreHistories = async () => {
    if (!hasNextPage || isFetchingMore) return;

    setIsFetchingMore(true);
    try {
      await fetchMore({
        variables: { first: PAGE_SIZE, after: endCursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.workerPointHistories?.edges) return prev;
          if (!prev?.workerPointHistories?.edges) return prev;

          return {
            workerPointHistories: {
              ...fetchMoreResult.workerPointHistories,
              edges: [
                ...prev.workerPointHistories.edges,
                ...fetchMoreResult.workerPointHistories.edges,
              ],
            },
          };
        },
      });
    } catch {
      showNotification({ type: "error", message: FETCH_ERROR_MESSAGE });
    } finally {
      setIsFetchingMore(false);
    }
  };

  return {
    edges,
    hasNextPage,
    loading,
    error,
    isFetchingMore,
    fetchMoreHistories,
  };
}
